/**
 * Shared helpers for the @mention feature in ConversationsInput.
 *
 * Pulled out of the component because the same three operations —
 * build the suggestion list, parse mentions back out of a sent value,
 * and expand "@all" into real member ids — are needed independently in
 * sendMessage, sendCommentsMessage, and editMessage. Keeping them here
 * means there's exactly one implementation of each to get right (and
 * to unit test), instead of three copies that can drift.
 *
 * Markup reminder: <Mention markup="@@@____id__^^____display__@@@^^^" />
 * means a mention of user 42 ("Jane Doe") is stored in the raw value as:
 *   @@@__42^^__Jane Doe@@@^^^
 */

export interface MentionableUser {
  id: string | number;
  display: string;
}

// Sentinel id/display for the "mention everyone" suggestion. Not a real
// user id, so anything that walks a mentions list needs to treat it
// specially (see expandMentions below) rather than passing it straight
// through to a notify/recipients list.
export const MENTION_ALL_ID = "all";
export const MENTION_ALL_DISPLAY = "All";

/**
 * Builds the array handed to <Mention data={...}>: real members minus
 * the current user, with a synthetic "All" entry prepended once there's
 * more than one other person to mention.
 *
 * With 0 or 1 other members, "mention everyone" is either meaningless
 * (DM, no one else) or identical to just mentioning that one person, so
 * it's left out — same as WhatsApp, which only offers @all in groups.
 *
 * Defensive on `members` because threadMembers/tenantMembers/userList
 * are all Redux state that starts out undefined before their fetch
 * resolves — a plain `.filter()` on that would throw on first render.
 */
export function buildMentionableUsers(
  members: MentionableUser[] | undefined | null,
  currentUserId: string | number
): MentionableUser[] {
  const others = (members || []).filter((m) => +m.id !== +currentUserId);

  if (others.length <= 1) {
    return others;
  }

  return [{ id: MENTION_ALL_ID, display: MENTION_ALL_DISPLAY }, ...others];
}

// Matches just the id portion of a mention token, e.g. captures "42" out
// of "@@@__42^^__Jane Doe@@@^^^". `[^^]+` (any run of non-caret chars)
// stops cleanly at the "^^__" separator regardless of what the display
// name contains, including spaces — unlike splitting the whole value on
// " ", which breaks as soon as a display name is more than one word.
const MENTION_TOKEN_PATTERN = "@@@__([^^]+)\\^\\^__";

/**
 * Pulls every mentioned id out of a raw MentionsInput value. Runs
 * against the whole string rather than splitting on spaces first, so
 * multi-word display names ("Jane Doe") can't cut a token in half.
 */
export function extractMentionedIds(rawValue: string): string[] {
  if (!rawValue) return [];

  // New RegExp per call rather than a shared module-level `g` regex:
  // exec() on a shared global regex advances its own lastIndex, so a
  // second call earlier in the same tick (or a re-render) can pick up
  // mid-string and silently miss matches. Cheap to avoid.
  const pattern = new RegExp(MENTION_TOKEN_PATTERN, "g");
  const ids: string[] = [];
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(rawValue)) !== null) {
    ids.push(match[1]);
  }
  return ids;
}

/**
 * Resolves a raw list of mentioned ids (as extracted above) into the
 * concrete ids to actually notify/record, expanding MENTION_ALL_ID into
 * every real member's id if present.
 *
 * `mentionableEntries` should be the same list passed to <Mention data>
 * — the synthetic "All" entry is filtered back out here so it never
 * leaks into a notification/recipient list as a literal id of "all".
 */
export function expandMentions(
  mentionedIds: string[],
  mentionableEntries: MentionableUser[]
): string[] {
  const realMemberIds = (mentionableEntries || [])
    .filter((m) => m.id !== MENTION_ALL_ID)
    .map((m) => String(m.id));

  if (mentionedIds.includes(MENTION_ALL_ID)) {
    return realMemberIds;
  }

  return Array.from(new Set(mentionedIds));
}
